/**
 * Magnetosphere CDN shim — a generic React wrapper for the `<boomi-*>` Custom
 * Elements delivered at runtime from the CDN (see `utils/magnetosphereCdn.ts`).
 *
 * These are NOT magnetosphere React components — the implementations stream from
 * the CDN at runtime, so a magnetosphere fix reaches the app with no rebuild.
 *
 * React 18 sets JSX props on a Custom Element as HTML attributes (strings), so
 * functions and objects are dropped. They are assigned as DOM properties via a
 * ref instead; the CDN adapter installs camelCase setters for every schema prop
 * (attributes, object/function properties and event callbacks alike). Hyphenated
 * props (`data-testid`, `aria-*`) are real attributes and are rendered as such.
 *
 * Only the components this app renders are exported. Do NOT special-case a
 * component inside `createWebComponent`; add a new `createWebComponent(tag, name)`
 * export instead.
 */
import React, { forwardRef, useEffect, useRef } from 'react';
import type { SerializeMode } from './magnetosphere-types';

/** Props React should render as DOM attributes rather than mirror as properties. */
const isAttributeProp = (key: string) => key.includes('-');

/**
 * Boolean props a component reads on its FIRST render — before the CDN adapter
 * delivers camelCase *properties* (set asynchronously after connectedCallback).
 * These must be present as declarative string attributes on that first render, so
 * map camelCase → kebab attribute name. Rendered as the STRING "true"/"false"
 * because React drops a `false` boolean attribute.
 *
 * `checkForDevelopment` (masthead): if absent on the first render the masthead
 * defaults `window.useProxy = true` and sends its initial LoadUserHandler to the
 * dev proxy (http://localhost:8010), so it renders an empty shell in production.
 */
const ATTRS_REQUIRED_ON_FIRST_RENDER: Record<string, string> = {
    checkForDevelopment: 'check-for-development',
    userIsAuthenticated: 'user-is-authenticated',
    showTemplateTiles: 'show-template-tiles',
    allowManualEdit: 'allow-manual-edit',
    enableSnowMigration: 'enable-snow-migration',
    preserveSearchHistory: 'preserve-search-history',
    disableTranslation: 'disable-translation',
    enableCustomSideBarTitle: 'enable-custom-side-bar-title',
};

/**
 * Wrap a `<boomi-*>` element in a proxy that guards imperative-handle access.
 * A component's methods are installed on the element only when its chunk upgrades
 * it, so on a cold load a handle call could throw "… is not a function". Any
 * not-yet-installed method becomes a no-op (→ undefined) and the real method is
 * forwarded once the element upgrades.
 */
function makeGuardedHandle(node: HTMLElement): HTMLElement {
    return new Proxy(node, {
        get(target, prop) {
            const value = Reflect.get(target, prop);
            if (typeof value === 'function') return value.bind(target);
            if (typeof prop === 'string' && prop !== 'then' && !(prop in target)) {
                return () => undefined;
            }
            return value;
        },
    });
}

/**
 * Build a React component that renders a `<boomi-*>` Custom Element. Hyphenated
 * props are rendered as attributes; every other prop is mirrored onto the element
 * as a DOM property on each render.
 */
function createWebComponent<P extends object, E extends HTMLElement = HTMLElement>(
    tagName: string,
    displayName: string,
) {
    const Component = forwardRef<E, P>((props, forwardedRef) => {
        const elementRef = useRef<HTMLElement | null>(null);

        const attributeProps: Record<string, unknown> = {};
        const propertyProps: Record<string, unknown> = {};
        Object.entries(props).forEach(([key, value]) => {
            if (key === 'children' || key === 'key' || key === 'ref') return;
            const firstRenderAttr = ATTRS_REQUIRED_ON_FIRST_RENDER[key];
            if (firstRenderAttr && typeof value === 'boolean') {
                attributeProps[firstRenderAttr] = String(value);
                return;
            }
            if (isAttributeProp(key)) {
                attributeProps[key] = value;
            } else {
                propertyProps[key] = value;
            }
        });

        // Only write a property when its value actually changed: the CDN adapter
        // exposes these as real setters, so re-assigning an identical handler every
        // render can re-trigger the element's side effects and loop infinitely if the
        // handler updates React state. Do not remove this guard.
        useEffect(() => {
            const element = elementRef.current as unknown as Record<string, unknown>;
            if (!element) return;
            Object.entries(propertyProps).forEach(([key, value]) => {
                if (element[key] !== value) {
                    element[key] = value;
                }
            });
        });

        return React.createElement(tagName, {
            ...attributeProps,
            ref: (node: HTMLElement | null) => {
                elementRef.current = node;
                const typed = (node && makeGuardedHandle(node)) as unknown as E | null;
                if (typeof forwardedRef === 'function') {
                    forwardedRef(typed);
                } else if (forwardedRef) {
                    (forwardedRef as { current: E | null }).current = typed;
                }
            },
        });
    });

    Component.displayName = displayName;
    return Component;
}

/** `<boomi-masthead>` — replacement for `Header` (MastHead). */
export interface HeaderProps {
    'data-testid'?: string;
    pageInfo?: unknown;
    checkForDevelopment?: boolean;
    platformUrl?: string;
    usePlatformUrlAsHost?: boolean;
    jwt?: string;
    subNav?: unknown;
    disableActiveNavHighlight?: boolean;
    disableMoreMenu?: boolean;
    disableTranslation?: boolean;
    suppressDefaultLandingPage?: boolean;
    noNeedAuthorization?: boolean;
    onNavigation?: (menu?: { id?: string }) => void;
    onSwitchAccountSuccess?: (payload: { accountId: string }) => void;
    onSignOutSuccess?: (historyToken: string, logoutRedirectUrl: string) => void;
    onAuthError?: () => void;
    showBoomiAi?: (isClicked: boolean) => void;
    searchIconEventHandler?: () => void;
}
export const Header = createWebComponent<HeaderProps>('boomi-masthead', 'Header');

/** `<boomi-aisearch>` — replacement for `AISearch`. */
export interface AISearchProps {
    'data-testid'?: string;
    openSearchPanel?: boolean;
    userIsAuthenticated?: boolean;
    jwt?: string;
    serializeMode?: SerializeMode;
    closeBtnEventHandler?: (isClicked: boolean) => void;
}
export const AISearch = createWebComponent<AISearchProps>('boomi-aisearch', 'AISearch');
